/*
 * Copyright (c) 2014-2023 Inovalon, Inc. All Rights Reserved.
 * This computer program is CONFIDENTIAL and a TRADE SECRET of Inovalon, Inc.
 * The receipt or possession of this program does not convey any rights to use, reproduce or disclose
 * its contents in whole or in part, without the specific written consent of Inovalon, Inc.
 * Any use, reproduction or disclosure of this program without the express written consent of Inovalon, Inc.
 * is a violation of the copyright laws and may subject you to criminal prosecution.
 */
import React, { Fragment } from 'react'
import { ICoverageDiscoveryResponseStateManager, IBaseResponse, IIndividual, IBaseSection, IDemographic } from '../../../services/CoverageDiscoveryResponseStateManager/contracts'
import { PatientSection } from '../DisplayTemplates/PatientSection/PatientSection'
import { ServiceCoverageSummaryComponent } from '../Medicare/ServiceCoverageSummary/ServiceCoverageSummary'
import { InpatientSummaryComponent } from '../DisplayTemplates/InpatientSummary/InpatientSummary'
import { HomeHospiceSummaryComponent } from '../DisplayTemplates/HomeHospiceSummary/HomeHospiceSummary'
import { TherapyCapSummary } from '../DisplayTemplates/TherapyCapSummary/TherapyCapSummary'
import { EligibilitySummaryComponent } from '../DisplayTemplates/EligibilitySummary/EligibilitySummary'
import { StatusAlertSection } from '../Medicare/StatusAlertSection/StatusAlertSection'
import { PreventativeSection } from '../DisplayTemplates/PreventativeSection/PreventativeSection'
import { SnfSection } from '../Medicare/SnfSection/SnfSection'
import { ServiceTypeSection } from '../Medicare/ServiceTypeSection/ServiceTypeSection'
import { BenefitInformationComponent } from '../Commercial/BenefitInformation/BenefitInformation'
import { CServiceCoverageSummaryComponent } from '../Commercial/CServiceCoverageSummary/CServiceCoverageSummary'
import { CEligibilitySummary } from '../Commercial/CEligibilitySummary/CEligibilitySummary'
import { OtherPayerAlertSection } from '../Commercial/OtherPayerAlertSection/OtherPayerAlertSection'
import { MCOStatusAlertSection } from '../Commercial/MCOStatusAlertSection/MCOStatusAlertSection'
import { PCPAlertSection } from '../Commercial/PCPAlertSection/PCPAlertSection'
import { CserviceTypeSummary } from '../Commercial/CserviceTypeSummary/CserviceTypeSummary'
import { PayerType } from '../../../configuration/Enums/PayerType'
import { Container, Row, Col } from 'reactstrap'
import { ICoverageDiscoveryTransaction } from '../../../services/CoverageDiscoveryTransactionStateManager/contracts'
import { ImmunizationSummaryComponent } from '../DisplayTemplates/ImmunizationSummary/ImmunizationSummary'

export interface IBaseSectionProps {
	cdTransaction?: ICoverageDiscoveryTransaction
	response: IBaseResponse
	CoverageDiscoveryResponseStateManager: ICoverageDiscoveryResponseStateManager
}

interface IBaseSectionState {
	sentSubscriberDetails?: IIndividual
	sentGuarantorDetails?: IIndividual
	receivedSubscriberDetails?: IIndividual
	demographicResponse?: IDemographic
	activeResponse?: IBaseResponse
	collapse: number
}

export class BaseSectionComponent extends React.Component<IBaseSectionProps, IBaseSectionState> {
	constructor(props: Readonly<IBaseSectionProps>) {
		super(props)
		this.state = {
			sentSubscriberDetails: this.props.cdTransaction && this.props.cdTransaction.transactionRequest.individual,
			sentGuarantorDetails: this.props.cdTransaction && this.props.cdTransaction.transactionRequest.guarantor,
			receivedSubscriberDetails: this.props.response && this.props.response.eligibilityResponseTransaction.receivedSubscriber,
			demographicResponse: this.props.cdTransaction && this.props.cdTransaction.transactionResponse && this.props.cdTransaction.transactionResponse.demographic,
			activeResponse: this.props.response,
			collapse: 0,
		}
	}

	renderMedicare(): any {
		return (
			<Fragment>
				{this.renderConfidenceScoreSection(this.props.response)}
				<PatientSection sentSubscriberDetails={this.state.sentSubscriberDetails} sentGuarantorDetails={this.state.sentGuarantorDetails} demographicResponse={this.state.demographicResponse} receivedSubscriberDetails={this.state.receivedSubscriberDetails} response={this.state.activeResponse} />
				<EligibilitySummaryComponent CoverageDiscoveryResponseStateManager={this.props.CoverageDiscoveryResponseStateManager} />
				<ImmunizationSummaryComponent CoverageDiscoveryResponseStateManager={this.props.CoverageDiscoveryResponseStateManager} />
				<ServiceCoverageSummaryComponent CoverageDiscoveryResponseStateManager={this.props.CoverageDiscoveryResponseStateManager} />
				<InpatientSummaryComponent CoverageDiscoveryResponseStateManager={this.props.CoverageDiscoveryResponseStateManager} />
				<HomeHospiceSummaryComponent CoverageDiscoveryResponseStateManager={this.props.CoverageDiscoveryResponseStateManager} />
				<TherapyCapSummary CoverageDiscoveryResponseStateManager={this.props.CoverageDiscoveryResponseStateManager} />
				<StatusAlertSection CoverageDiscoveryResponseStateManager={this.props.CoverageDiscoveryResponseStateManager} />
				<ServiceTypeSection CoverageDiscoveryResponseStateManager={this.props.CoverageDiscoveryResponseStateManager} />
				<PreventativeSection CoverageDiscoveryResponseStateManager={this.props.CoverageDiscoveryResponseStateManager} />
				<SnfSection CoverageDiscoveryResponseStateManager={this.props.CoverageDiscoveryResponseStateManager} />
			</Fragment>
		)
	}
	renderConfidenceScoreSection(response: IBaseResponse | null): any {
		if (typeof response !== 'undefined' && response !== null) {
			const confidenceScore = response.eligibilityResponseTransaction.confidenceScore

			if (typeof confidenceScore !== 'undefined' && confidenceScore !== null) {
				return (
					<Container className="patientdetails">
						<Row className="header" style={{ backgroundColor: confidenceScore.code && confidenceScore.code.toLowerCase() === 'y' ? '#aaba0a' : '#deb406' }}>
							<Col xs="3" style={{ color: 'white', padding: '1px' }}>
								Coverage Confidence Score:
							</Col>
							<Col xs="9" style={{ color: 'white', padding: '1px' }}>
								{confidenceScore.code && confidenceScore.code} - {confidenceScore.description && confidenceScore.description}
								{confidenceScore.code && confidenceScore.code.toLowerCase() === 'r' && response.eligibilityResponseTransaction.confidenceScoreReason && ' (' + response.eligibilityResponseTransaction.confidenceScoreReason + ')'}
							</Col>
						</Row>
					</Container>
				)
			}
		}
	}

	renderCommercialMedicaid() {
		return (
			<Fragment>
				{this.renderConfidenceScoreSection(this.props.response)}
				<PatientSection sentSubscriberDetails={this.state.sentSubscriberDetails} sentGuarantorDetails={this.state.sentGuarantorDetails} demographicResponse={this.state.demographicResponse} receivedSubscriberDetails={this.state.receivedSubscriberDetails} response={this.state.activeResponse} />
				<CEligibilitySummary CoverageDiscoveryResponseStateManager={this.props.CoverageDiscoveryResponseStateManager} />
				<CServiceCoverageSummaryComponent CoverageDiscoveryResponseStateManager={this.props.CoverageDiscoveryResponseStateManager} />
				<OtherPayerAlertSection CoverageDiscoveryResponseStateManager={this.props.CoverageDiscoveryResponseStateManager} />
				<MCOStatusAlertSection CoverageDiscoveryResponseStateManager={this.props.CoverageDiscoveryResponseStateManager} />
				<PCPAlertSection CoverageDiscoveryResponseStateManager={this.props.CoverageDiscoveryResponseStateManager} />
				<BenefitInformationComponent CoverageDiscoveryResponseStateManager={this.props.CoverageDiscoveryResponseStateManager} />
				<CserviceTypeSummary CoverageDiscoveryResponseStateManager={this.props.CoverageDiscoveryResponseStateManager} />
			</Fragment>
		)
	}

	renderMediSwitchParam(payerTypeId: number) {
		switch (payerTypeId) {
			case PayerType.Medicare:
				return this.renderMedicare()
			case PayerType.Medicaid:
			case PayerType.Commercial:
				return this.renderCommercialMedicaid()
			default:
				break
		}
	}

	render(): React.ReactNode {
		const model = this.props.response
		if (typeof model !== 'undefined' && model !== null && typeof model.sections !== 'undefined' && model.sections !== null && model.sections.length > 0) {
			const payerTypeId = model.eligibilityResponseTransaction.payer && model.eligibilityResponseTransaction.payer.payerTypeId
			return <div>{payerTypeId && this.renderMediSwitchParam(payerTypeId)}</div>
		}
		return null
	}
}
